import { always, prop } from 'ramda'

import { useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import type { Breakpoint } from '@mui/material/styles'

import { breakpoints } from '~/src/UI/Theme/basics'

import getGlobal from '../getGlobal'

// getGlobal is untyped JS and can only return null when no global object exists; the fallback narrows its type
const global = getGlobal() ?? globalThis

// The keys in UI/Theme/basics are declared as plain strings
const isBreakpoint = (key: string): key is Breakpoint => ['xs', 'sm', 'md', 'lg', 'xl'].includes(key)

/**
 * Prevents default behavior of HTML element
 *
 * @returns Called preventDefault or undefined
 */
export const preventDefault = (event?: Partial<Pick<Event, 'preventDefault'>> | null): void => event?.preventDefault?.()

/** Replaces mustache pattern {{ anything }} with data value provided */
export const templateRenderer = (template: string, data: Record<string, unknown>): string =>
  template.replace(/{{\s*(\w+)\s*}}/g, (m, key: string) => (data[key] != null ? String(data[key]) : ''))

/** Returns bound templateRenderer function with a template as 1 argument */
export const compileTemplate = (template: string) => templateRenderer.bind(null, template)

/**
 * Sanitizes strings that _may_ contain sub/sup html tags
 *
 * @param input - A string that may contain HTML
 * @returns String - a string with all HTML tags except sub/sup removed
 */
export const dtLabelSanitizer = (string?: string | null): string =>
  string ? string.replace(/(<[^>]+>)/g, match => (/<\/?(span|sub|sup|u)>/.test(match) ? match : '')) : ''

/**
 * Create a collision detector suitable for map/reduce
 *
 * @param edge Either the property name from which to get the edge value or a function which returns the edge value
 * @param length Either a length value or a function which returns the length value
 * @returns Boolean
 */
export const getCollidesWith = <K extends string, T extends Record<K, number>>(
  edge: K | ((item: T) => number),
  length: number | ((item: T) => number),
) => {
  const edgeGetter = typeof edge === 'function' ? edge : prop(edge)
  const lengthGetter = typeof length === 'function' ? length : always(length)

  return (a: T, b: T): boolean => {
    const aEdge = edgeGetter(a)
    const bEdge = edgeGetter(b)
    const aLength = lengthGetter(a)
    const bLength = lengthGetter(b)

    const collides =
      // a's low edge collides with b's high edge
      // b's low edge collides with a's high edge
      (aEdge <= bEdge && aEdge + aLength >= bEdge) || (aEdge >= bEdge && aEdge <= bEdge + bLength)
    return collides
  }
}
/** Determines current window width breakpoint. `sm` covers 600-959px. */
export const useBreakpoint = (): Breakpoint => {
  const theme = useTheme()
  if (!breakpoints?.keys) return 'xs'

  const keys = [...breakpoints.keys].reverse().filter(isBreakpoint)

  return (
    keys.reduce<Breakpoint | null>((output, key) => {
      const matches = useMediaQuery(theme.breakpoints.only(key))

      return !output && matches ? key : output
    }, null) ?? 'xs'
  )
}

/**
 * Determines whether current window width lower or equal to {max} breakpoint provided
 *
 * Inclusive: `'sm'` means the sm tier or smaller, i.e. width < 960px. `theme.breakpoints.down('sm')` and
 * `useMatchesBreakpoint('down', 'sm')` are exclusive, width < 600px. This is not mobile detection; see
 * `~/src/Lib/mobileLayout`.
 */
export const useIsBreakpointDown = (max: Breakpoint, breakpoint: Breakpoint): boolean => {
  if (!breakpoints?.keys) return false

  const keys = [...breakpoints.keys].reverse()

  if (!keys.includes(max)) {
    throw new Error(`Invalid breakpoint provided. Valid breakpoints are: ${keys.join(', ')}`)
  }

  return keys.indexOf(max) <= keys.indexOf(breakpoint)
}

type MatchesBreakpointArgs =
  | readonly ['between', Breakpoint, Breakpoint]
  | readonly ['up' | 'down' | 'only', Breakpoint]

/** Determines whether current window width is in the range of the breakpoint(s) provided */
export const useMatchesBreakpoint = (...[range, breakpoint1, breakpoint2]: MatchesBreakpointArgs): boolean => {
  const allowedRanges = ['up', 'down', 'only', 'between']
  const theme = useTheme()

  if (!range || !breakpoint1) {
    throw new Error('Expected range and at least one breakpoint parameter.')
  }

  if (range !== 'between' && breakpoint2) {
    throw new Error('Expected only one breakpoint parameter but had two.')
  }

  if (range === 'between' && !breakpoint2) {
    throw new Error('Expected second breakpoint parameter but had only one.')
  }

  if (!breakpoints.keys.includes(breakpoint1) || (breakpoint2 && !breakpoints.keys.includes(breakpoint2))) {
    throw new Error(`Invalid breakpoint provided. Valid breakpoints are: ${breakpoints.keys.join(', ')}`)
  }

  if (!allowedRanges.includes(range)) {
    throw new Error(`Invalid range provided. Valid breakpoints are: ${allowedRanges.join(', ')}`)
  }
  const matches = useMediaQuery(
    range === 'between' ? theme.breakpoints.between(breakpoint1, breakpoint2) : theme.breakpoints[range](breakpoint1),
  )
  return matches
}

/**
 * Get root element font size for rem-based calculations
 *
 * @returns Root element font size in CSS pixels
 */
export const getRemSize = (): number => {
  const { document } = global
  if (!document) return 16
  const root = document.querySelector(':root')
  if (!root) return 16
  const fontSize = global.getComputedStyle(root).getPropertyValue('font-size')

  return Number(fontSize.replace('px', '')) || 16
}

/** Returns element's ancestor tree */
const parents = (node: Node, ps: Node[] = []): Node[] =>
  node.parentNode === null ? ps : parents(node.parentNode, ps.concat(node))

const scrollingPattern = /auto|scroll/
/** Tests whether a given element is a scroll context */
const scrollCheck = (node: Node): node is Element => {
  if (!(node instanceof Element)) return false
  const computed = global.getComputedStyle(node, null)

  return scrollingPattern.test(computed.overflow || computed.overflowY || computed.overflowX || '')
}

/** Returns the nearest ancestor that is a scroll context */
export const getScrollContexts = (element: Element | null | undefined): Array<Element | typeof globalThis | null> => {
  if (element instanceof HTMLElement) {
    const tree = parents(element)
    return tree.filter(scrollCheck)
  }
  return [document.querySelector('main#content'), global]
}
